import { useMemo } from 'react';
import { useWindowDimensions } from 'react-native';
import { FadeIn } from '../../../components/animations';
import { type ColoredDataPoint, DonutChart } from '../../../components/charts';
import { Box } from '../../../components/primitives/Box';
import { HStack, Stack, VStack } from '../../../components/primitives/Stack';
import { Text } from '../../../components/primitives/Text';
import { useAuth } from '../../../contexts/AuthContext';
import { useTranslation } from '../../../contexts/TranslationContext';
import { useDeviceTypeLabel } from '../../../hooks/useDeviceTypeLabel';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import { type DateRange, getDateRange, type TimeRange } from '../hooks/useAnalyticsData';
import {
  useDeviceBreakdown,
  useGeoPerformance,
  useSourcePerformance,
} from '../hooks/useCampaignAnalytics';
import { TabEmptyState, TabErrorState, TabLoadingState } from './AnalyticsLoadingState';
import { ChartCard } from './ChartCard';
import { SourcePerformanceTable } from './SourcePerformanceTable';

interface CampaignsTabProps {
  timeRange: TimeRange;
}

const DEVICE_COLORS: Record<string, string> = {
  mobile: colors.primary.neon,
  desktop: colors.accent.purple.neon,
  tablet: colors.accent.amber.neon,
};

export function CampaignsTab({ timeRange }: Readonly<CampaignsTabProps>) {
  const { currentOrganization } = useAuth();
  const deviceTypeLabel = useDeviceTypeLabel();
  const { t } = useTranslation();
  const orgId = currentOrganization?.id;
  const dateRange: DateRange = useMemo(() => getDateRange(timeRange), [timeRange]);

  const {
    data: sourcePerformance,
    isLoading,
    isError,
    refetch,
  } = useSourcePerformance(orgId, dateRange);
  const { data: devices } = useDeviceBreakdown(orgId, dateRange);
  const { data: geoData } = useGeoPerformance(orgId, dateRange);

  const { width } = useWindowDimensions();
  const isDesktop = width >= tokens.breakpoints.desktop;

  if (isLoading) return <TabLoadingState />;
  if (isError) return <TabErrorState onRetry={refetch} />;

  const deviceChartData: ColoredDataPoint[] = (devices ?? []).map((d) => ({
    label: deviceTypeLabel(d.deviceType),
    value: d.leads,
    color: DEVICE_COLORS[d.deviceType.toLowerCase()] ?? colors.text.muted,
  }));

  const totalDeviceLeads = deviceChartData.reduce((sum, d) => sum + d.value, 0);

  return (
    <VStack spacing="xl">
      {sourcePerformance && sourcePerformance.length > 0 && (
        <FadeIn delay={100}>
          <ChartCard
            title={t('analytics.campaigns.sourcePerformance.title')}
            subtitle={t('analytics.campaigns.sourcePerformance.subtitle')}
            accentColor="cyan"
            delay={150}
          >
            <SourcePerformanceTable data={sourcePerformance} />
          </ChartCard>
        </FadeIn>
      )}

      <FadeIn delay={300}>
        <Stack direction={isDesktop ? 'horizontal' : 'vertical'} spacing="lg">
          {deviceChartData.length > 0 && (
            <Box style={isDesktop ? { flex: 1, flexBasis: 0 } : undefined}>
              <ChartCard
                title={t('analytics.campaigns.deviceBreakdown.title')}
                subtitle={t('analytics.campaigns.deviceBreakdown.subtitle')}
                accentColor="cyan"
                delay={350}
              >
                <Box alignItems="center" justifyContent="center" style={{ minHeight: 180 }}>
                  <DonutChart
                    data={deviceChartData}
                    size={130}
                    strokeWidth={18}
                    showLegend
                    centerValue={totalDeviceLeads.toString()}
                    centerLabel={t('analytics.campaigns.deviceBreakdown.centerLabel')}
                  />
                </Box>
              </ChartCard>
            </Box>
          )}

          {geoData && geoData.length > 0 && (
            <Box style={isDesktop ? { flex: 1, flexBasis: 0 } : undefined}>
              <ChartCard
                title={t('analytics.campaigns.geo.title')}
                subtitle={t('analytics.campaigns.geo.subtitle')}
                accentColor="purple"
                delay={400}
              >
                <GeoTable data={geoData} />
              </ChartCard>
            </Box>
          )}
        </Stack>
      </FadeIn>

      {!sourcePerformance?.length && !deviceChartData.length && !geoData?.length && (
        <TabEmptyState message={t('analytics.campaigns.empty')} />
      )}
    </VStack>
  );
}

function GeoTable({
  data,
}: Readonly<{
  data: Array<{
    country: string;
    leads: number;
    percentage: number;
  }>;
}>) {
  const { t } = useTranslation();
  const sorted = [...data].sort((a, b) => b.leads - a.leads);

  return (
    <VStack spacing="sm">
      {sorted.map((geo, index) => (
        <FadeIn key={geo.country} delay={index * 40}>
          <HStack
            spacing="md"
            align="center"
            justify="between"
            style={{
              paddingVertical: 8,
              paddingHorizontal: 12,
              backgroundColor: index % 2 === 0 ? colors.surface.row : 'transparent',
              borderRadius: 8,
            }}
          >
            <Text variant="body" color={colors.text.primary} style={{ fontWeight: '500', flex: 2 }}>
              {geo.country}
            </Text>
            <Text variant="body" color={colors.text.secondary} style={{ flex: 1 }}>
              {t('analytics.campaigns.geo.leads', { count: geo.leads })}
            </Text>
            <Text variant="caption" color={colors.text.tertiary} style={{ flex: 0.5 }}>
              {geo.percentage}%
            </Text>
          </HStack>
        </FadeIn>
      ))}
    </VStack>
  );
}
